import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Shared trigger logic for the travel-needs assessment, so the same behaviour
 * can drive the homepage and the vestibule destination pages (Aircraft,
 * Management).
 *
 * How it surfaces:
 *   • It opens ONCE, the first time the visitor leaves the intro — via the
 *     Skip button, the video's natural end, scrolling down into the page, or
 *     (on the destination pages) arriving from the vestibule.
 *   • After that first appearance it does NOT auto-interrupt again. Instead a
 *     small persistent tab on the right edge (`showTab`) lets anyone re-open it
 *     — including visitors who closed or opted out the first time.
 *
 * `onContinue(reason, source)` runs after the modal closes, letting each page
 * decide what happens next (e.g. the homepage glides into the map). Scroll-,
 * tab-, and vestibule-triggered opens leave the visitor where they are.
 */
export default function useTravelAssessment({ autoOpenFromVestibule = false, openOnScroll = true, onContinue } = {}) {
  const [open, setOpen] = useState(false);
  // "Shown once" persists for the whole browser session, so the window
  // auto-surfaces a single time and NEVER re-triggers when switching pages —
  // the tab simply follows the visitor across the site instead.
  const [hasBeenShown, setHasBeenShown] = useState(() => {
    try { return sessionStorage.getItem("steck_assessment_shown") === "1"; } catch (e) { return false; }
  });
  const [, setLastPayload] = useState(null);               // completed payload, local state only

  const openRef = useRef(false);              // synchronous mirror of `open`
  const sourceRef = useRef(null);             // how the current open was triggered
  const hasOpenedRef = useRef(hasBeenShown);  // seeded from the session flag above

  const openAssessment = useCallback((source) => {
    if (openRef.current) return false;
    // The window auto-surfaces exactly ONCE. After that, only the persistent
    // tab may re-open it — Skip, the video ending, scrolling, or arriving again
    // must never pop the window a second time.
    if (hasOpenedRef.current && source !== "tab") return false;
    openRef.current = true;
    sourceRef.current = source;
    hasOpenedRef.current = true;
    try { sessionStorage.setItem("steck_assessment_shown", "1"); } catch (e) { /* ignore */ }
    setHasBeenShown(true);
    setOpen(true);
    return true;
  }, []);

  const onContinueRef = useRef(onContinue);
  useEffect(() => { onContinueRef.current = onContinue; }, [onContinue]);

  const closeAssessment = useCallback((reason) => {
    const source = sourceRef.current;
    openRef.current = false;
    setOpen(false);
    if (onContinueRef.current) {
      // Defer so the modal's scroll-lock restores position before any
      // programmatic scroll a page might run on continue.
      requestAnimationFrame(() => onContinueRef.current(reason, source));
    }
  }, []);

  // FUTURE BACKEND INTEGRATION POINT ──────────────────────────────────────────
  // Single place to later send the completed assessment to Steck. `payload` is
  // ready to POST: { workProfile, primaryFriction, impactScore, resultProfile,
  // completedAt, source }. Nothing is sent today — kept in local state only.
  const onComplete = useCallback((payload) => {
    setLastPayload(payload);
  }, []);

  // First-engagement scroll trigger: open once when the visitor scrolls down
  // into the page. After it has opened once, the tab takes over (no re-pop).
  // This is a homepage-intro behaviour; pages that opt out (openOnScroll:false)
  // never auto-open on scroll, so scrolling them is never interrupted.
  useEffect(() => {
    if (!openOnScroll) return undefined;
    const onScroll = () => {
      if (openRef.current || hasOpenedRef.current) return;
      const vh = window.innerHeight || 800;
      if (window.scrollY > vh * 0.6) openAssessment("scroll");
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [openAssessment, openOnScroll]);

  // Vestibule destination pages: open shortly after arrival if the visitor came
  // from the vestibule. Uses a dedicated flag (the navbar consumes its own
  // "steck_from_vestibule" on mount, so we can't share it).
  useEffect(() => {
    if (!autoOpenFromVestibule) return undefined;
    let pending = false;
    try { pending = sessionStorage.getItem("steck_assessment_pending") === "1"; } catch (e) { /* ignore */ }
    if (!pending) return undefined;
    const t = setTimeout(() => {
      try { sessionStorage.removeItem("steck_assessment_pending"); } catch (e) { /* ignore */ }
      openAssessment("vestibule");
    }, 1300);
    return () => clearTimeout(t);
  }, [autoOpenFromVestibule, openAssessment]);

  return {
    open,
    source: sourceRef.current,
    showTab: hasBeenShown && !open,
    openAssessment,
    closeAssessment,
    onComplete,
  };
}
